import React, { forwardRef, ReactNode, HTMLAttributeAnchorTarget } from 'react';
import { useHover } from 'react-aria';
import { DOMProps, HoverEvents } from '@react-types/shared';
import { isMobile } from '@direct-frontend/platform-code';

import type { RouterProps } from '../../../internal/types/routerTypes';
import { Link } from '../../Link';
import { cn } from '../../../lib/css/className/className';
import { HStack } from '../../Stack';
import { IconArrowRight, IconBeta } from '../../../icons';
import { useInternalVerticalAlign } from '../../../hooks/useVerticalAlign/useVerticalAlign';
import { SidebarPopup } from '../SidebarPopup';
import { useSidebarCollapsedState } from '../SidebarCollapsedStateProvider';
import { useSidebarOpenedItemId } from '../SidebarPopupStateProvider';
import { TestProps } from '../../../internal/types/testTypes';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { SidebarPopupOpenTrigger, SidebarPopupProps } from '../SidebarPopup/types';
import { VerticalAlignAddonSize, VerticalAlignTextPreset } from '../../../hooks/useVerticalAlign/types';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';

import './SidebarItemGeneric.css';

export const cnSidebarItem = cn('SidebarItem');

const ItemTextSize: VerticalAlignTextPreset = '14-20';
const ItemAddonSize: VerticalAlignAddonSize = '16';

export interface SidebarItemGenericProps
    extends TestProps,
        DOMProps,
        HoverEvents,
        RouterProps,
        Pick<SidebarPopupProps, 'placement'> {
    /**
     * Контент, который нарисуется внутри триггера
     */
    content: ReactNode;
    /**
     * Элемент, описывающий вложенный уровень пунктов, открывающийся по клику на триггер
     */
    children?: ReactNode | ((closePopup: VoidFunction) => ReactNode);
    /**
     * Идентификатор пункта меню
     */
    itemId: string;
    /**
     * Дополнительный класс для элемента сайдбара
     */
    className?: string;
    /**
     * Выбран ли пункт меню
     */
    isActive?: boolean;
    /**
     * Признак отключенного пункта.
     * У отключенного пункта серый цвет текста и иконки, отключен ховер и не происходит действие на клик
     */
    isDisabled?: boolean;
    /**
     * Поведение при большом количестве текста – обрезание текста с многоточием или перенос на новую строку.
     * По умолчанию – перенос на новую строку, в свернутом виде отключено
     * @internal
     */
    isTruncated?: boolean;
    /**
     * Указатель, что пункт сайдбара относится к бета-версии раздела
     */
    isBeta?: boolean;
    /**
     * Ссылка пункта меню – если есть переход на новый URL
     */
    href?: string;
    /**
     * target пункта меню
     */
    target?: HTMLAttributeAnchorTarget;
    /**
     * Пропсы для триггера
     * @internal
     */
    triggerProps?: {
        /** Дополнительный класс для триггера */
        triggerClassName?: string;
        /** Способ открытия триггера */
        openTrigger?: SidebarPopupOpenTrigger;
        /** Коллбек клика по кнопке, если переданы пункты меню, будет вызван после открытия меню кнопки */
        onClick?: VoidFunction;
    };
    /**
     * Имеется ли подпись иконки в свернутом состоянии
     * @internal
     */
    hasIconLabel?: boolean;
    /**
     * Заголовок для Drawer в мобильной версии компонента
     */
    label?: string;
    /**
     * Аддон справа от контента, по клику на него ничего не происходит
     */
    addonRight?: ReactNode;
    /**
     * Должен ли контент расти до паддингов,
     * чтобы ссылка росла до бордеров
     * и при передаче addonRight аддон был справа, а слева полностью ссылка
     * @default true
     * @internal
     */
    shouldContentGrow?: boolean;
}

export const SidebarItemGeneric = forwardRef<HTMLElement, SidebarItemGenericProps>(
    function SidebarItemGeneric(props, ref) {
        const {
            'data-testid': dataTestId,
            isTruncated: isTruncatedProp,
            content,
            itemId,
            isActive = false,
            isDisabled,
            isBeta,
            href,
            target,
            routerOptions,
            children,
            className,
            triggerProps,
            hasIconLabel,
            label,
            placement,
            addonRight,
            shouldContentGrow = true,
        } = props;
        const { isCollapsed } = useSidebarCollapsedState();

        const openedMenuId = useSidebarOpenedItemId();
        const isOpen = itemId === openedMenuId;

        const { hoverProps, isHovered: isHoveredRes } = useHover({ ...props, isDisabled: false });
        const isHovered = !isDisabled && !isActive && (isHoveredRes || isOpen);

        const sizeSkinClass = getSizeSkin('s');
        const colorSkinClass = getColorSkin(isDisabled ? 'gray-transparent' : 'gray-neutral', {
            isHovered,
            isDisabled,
        });

        const isTruncated = !isCollapsed && isTruncatedProp;

        const { addonClasses: alignAddonClass } = useInternalVerticalAlign({
            textSize: ItemTextSize,
            addonSize: ItemAddonSize,
        });

        const shouldDrawAddon = !isMobile() && !isCollapsed && Boolean(addonRight);

        const itemClassName = cnSidebarItem(
            '',
            {
                active: isActive && !shouldDrawAddon,
                hovered: isHovered && !shouldDrawAddon,
                collapsed: isCollapsed,
                hasLabel: hasIconLabel,
                hasAddonRight: shouldDrawAddon,
            },
            [colorSkinClass, sizeSkinClass, !shouldDrawAddon ? className : undefined],
        );

        /* самое сердце item'a */
        let itemContent = (
            <HStack
                {...(!shouldDrawAddon ? hoverProps : {})}
                ref={ref}
                className={itemClassName}
                data-testid={dataTestId}
                justifyContent={isCollapsed ? 'center' : 'space-between'}
                alignItems="center"
            >
                <HStack
                    gap="4"
                    className={cnSidebarItem('content', {
                        truncated: isTruncated,
                        grow: shouldContentGrow,
                    })}
                >
                    {href || isCollapsed ? (
                        <>{content}</>
                    ) : (
                        <span className={cnSidebarItem('content', { truncated: isTruncated })}>{content}</span>
                    )}
                    {isBeta && !isCollapsed ? <IconBeta className={alignAddonClass} size="16" /> : null}
                </HStack>
                {isMobile() && Boolean(children) ? (
                    <IconArrowRight className={alignAddonClass} color="gray" size="16" />
                ) : null}
            </HStack>
        );

        // Оборачиваем в ссылку, если:
        // - для мобильной версии: у пункта есть ссылка и нет вложенных элементов (чтобы открывался попап с вложенными)
        // - для десктопной версии: если у пункта есть ссылка вне зависимости от наличия вложенных элементов
        const needToWrapInLink = !isDisabled && (isMobile() ? Boolean(href && !children) : Boolean(href));

        if (needToWrapInLink) {
            itemContent = (
                <Link
                    target={target ?? '_self'}
                    href={href}
                    routerOptions={routerOptions}
                    className={cnSidebarItem('link', { truncated: isTruncated })}
                    color="main"
                    data-testid={`SidebarLink.${itemId}`}
                    logNode={`SidebarLink.${itemId}`}
                >
                    {itemContent}
                </Link>
            );
        } else if (triggerProps?.onClick && !children) {
            itemContent = (
                <button
                    onClick={triggerProps.onClick}
                    className={cnSidebarItem('button', {}, [triggerProps?.triggerClassName])}
                >
                    {itemContent}
                </button>
            );
        }

        //добавляем аддон справа, если передан
        if (shouldDrawAddon)
            itemContent = (
                <HStack
                    {...hoverProps}
                    className={cnSidebarItem(
                        'container',
                        {
                            active: isActive,
                            hovered: isHovered,
                        },
                        [colorSkinClass, className],
                    )}
                >
                    {itemContent}
                    <HStack className={cnSidebarItem('addonRight')}>{addonRight}</HStack>
                </HStack>
            );

        if (!children) {
            return itemContent;
        }

        return (
            <SidebarPopup
                data-testid={`SidebarPopup.${itemId}`}
                id={itemId}
                content={children}
                isDisabled={isDisabled}
                title={label}
                placement={placement}
                logNode={`SidebarPopup.${itemId}`}
                {...triggerProps}
            >
                {itemContent}
            </SidebarPopup>
        );
    },
);
